// client/src/components/plataforma/siteInstitucional/TestimonialsManagementPage.js
import React, { useState, useEffect } from 'react';
import { useTestimonials } from '../../../hooks/useTestimonials';
import useAlert from '../../../hooks/useAlert';
import { CheckCircle, XCircle, Edit, Trash2, Loader2, MessageSquareQuote, Search, Star, Globe, Building, MoreVertical } from 'lucide-react';
import PageHeader from '../../layout/PageHeader';
import FormModal from '../../layout/FormModal';
import ActionsDrawer from '../../common/ActionsDrawer';

const TestimonialsManagementPage = () => {
    const { testimonials, loading, error, fetchAdminTestimonials, adminUpdateTestimonial, adminDeleteTestimonial } = useTestimonials();
    const { setAlert } = useAlert();

    const [filters, setFilters] = useState({ status: '', search: '', type: '' });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentTestimonial, setCurrentTestimonial] = useState(null);
    const [selectedTestimonial, setSelectedTestimonial] = useState(null);

    useEffect(() => {
        fetchAdminTestimonials(filters);
    }, [filters]);

    const handleFilterChange = e => setFilters({ ...filters, [e.target.name]: e.target.value });
    const handleSearch = e => e.preventDefault();

    const handleEdit = (testimonial) => {
        setCurrentTestimonial({ ...testimonial });
        setIsModalOpen(true);
        setSelectedTestimonial(null);
    };

    const handleStatusChange = async (id, status) => {
        try {
            await adminUpdateTestimonial(id, { status });
            fetchAdminTestimonials(filters);
            setSelectedTestimonial(null);
        } catch (err) {
            // O hook já mostra o alerta de erro
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Tem certeza que deseja excluir este depoimento permanentemente?')) {
            try {
                await adminDeleteTestimonial(id);
                setSelectedTestimonial(null);
            } catch(err) {
                // O hook já mostra o alerta de erro
            }
        }
    };
    
    const renderStatusBadge = (status) => {
        const styles = {
            pending: 'bg-yellow-100 text-yellow-800',
            approved: 'bg-green-100 text-green-800',
            rejected: 'bg-red-100 text-red-800',
        };
        return <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>{status}</span>;
    };

    const renderTypeBadge = (type) => {
        const styles = {
            platform: 'bg-blue-100 text-blue-800',
            account: 'bg-purple-100 text-purple-800',
        };
        const icons = {
            platform: <Globe size={12} className="inline mr-1" />,
            account: <Building size={12} className="inline mr-1" />
        };
        const labels = {
            platform: 'Plataforma',
            account: 'Conta'
        };
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${styles[type] || 'bg-gray-100'}`}>
                {icons[type]}
                {labels[type]}
            </span>
        );
    };
    
    return (
        <div className="page-management-container">
            <PageHeader
                title="Gerenciar Depoimentos"
                icon={<MessageSquareQuote className="icon-title" />}
                navBack={{ to: '/site-institucional', text: 'Voltar' }}
            />
            
            <div className="content-panel mt-6">
                <form onSubmit={handleSearch} className="flex flex-wrap items-center gap-4 mb-6">
                    <input 
                        type="text" 
                        name="search" 
                        value={filters.search} 
                        onChange={handleFilterChange} 
                        placeholder="Buscar por autor ou texto..." 
                        className="form-input flex-grow" 
                    />
                    <select 
                        name="status" 
                        value={filters.status} 
                        onChange={handleFilterChange} 
                        className="form-input w-full md:w-auto"
                    >
                        <option value="">Todos os Status</option>
                        <option value="pending">Pendentes</option>
                        <option value="approved">Aprovados</option>
                        <option value="rejected">Rejeitados</option>
                    </select>
                    <select 
                        name="type" 
                        value={filters.type} 
                        onChange={handleFilterChange} 
                        className="form-input w-full md:w-auto"
                    >
                        <option value="">Todos os Tipos</option>
                        <option value="platform">Plataforma</option>
                        <option value="account">Conta</option>
                    </select>
                </form>

            </div>

            {/* ✅ ACHADO: table-th/table-tr/table-td não existem em lugar
                nenhum do SCSS (grep confirmado) — a tabela inteira rodava
                sem nenhum estilo dessas classes, só o que min-w-full/bg-white
                do Tailwind davam. Convertido pro padrão table-container /
                table-wrapper / styled-table (mesmo padrão documentado no
                CLAUDE.md e usado em todo o resto do app). */}
            <div className="table-container">
                <div className="table-wrapper">
                    {loading ? (
                        <div className="table-loading-state">
                            <Loader2 size={24} className="animate-spin" />
                            <span>Carregando depoimentos...</span>
                        </div>
                    ) : error ? (
                        <div className="alert alert-danger">{error}</div>
                    ) : testimonials.length > 0 ? (
                        <table className="styled-table">
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left' }}>Autor</th>
                                    <th style={{ textAlign: 'left' }}>Depoimento</th>
                                    <th>Avaliação</th>
                                    <th>Tipo</th>
                                    <th>Status</th>
                                    <th className="text-right">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {testimonials.map(t => (
                                    <tr key={t._id}>
                                        <td style={{ textAlign: 'left' }}>
                                            <div className="flex items-center gap-3">
                                                <img src={t.avatarUrl} alt={t.authorName} className="w-10 h-10 rounded-full object-cover"/>
                                                <div>
                                                    <p className="font-semibold">{t.authorName}</p>
                                                    <p className="text-sm text-gray-500">{t.companyName}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td style={{ textAlign: 'left' }} className="max-w-sm">
                                            <p className="truncate">{t.quote}</p>
                                        </td>
                                        <td>
                                            {t.rating && (
                                                <div className="flex justify-center">
                                                    {[...Array(t.rating)].map((_, i) => (
                                                        <Star key={i} size={16} className="text-yellow-400 fill-current" />
                                                    ))}
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            {renderTypeBadge(t.type)}
                                        </td>
                                        <td>{renderStatusBadge(t.status)}</td>
                                        <td className="text-right">
                                            <button
                                                onClick={() => setSelectedTestimonial(t)}
                                                className="action-icon-button text-gray-600"
                                                title="Ações"
                                            >
                                                <MoreVertical size={18}/>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className="table-empty-state">
                            <MessageSquareQuote size={48} />
                            <h3>Nenhum depoimento encontrado</h3>
                        </div>
                    )}
                </div>
            </div>

            {isModalOpen && currentTestimonial && (
                <FormModal title="Editar Depoimento" onClose={() => setIsModalOpen(false)}>
                    <div className="p-4">
                        <p className="text-gray-600">Funcionalidade de edição detalhada em desenvolvimento.</p>
                        <div className="mt-4">
                            <pre className="bg-gray-100 p-3 rounded text-sm">
                                {JSON.stringify(currentTestimonial, null, 2)}
                            </pre>
                        </div>
                        <div className="form-actions">
                            <button
                                type="button"
                                onClick={() => setIsModalOpen(false)}
                                className="action-button action-button-secondary"
                            >
                                Fechar
                            </button>
                        </div>
                    </div>
                </FormModal>
            )}

            <ActionsDrawer
                open={!!selectedTestimonial}
                onClose={() => setSelectedTestimonial(null)}
                title={selectedTestimonial?.authorName || 'Depoimento'}
                subtitle={selectedTestimonial?.companyName}
                actions={selectedTestimonial ? [
                    ...(selectedTestimonial.status !== 'approved' ? [{
                        key: 'aprovar',
                        label: 'Aprovar',
                        icon: <CheckCircle size={20} className="icon" />,
                        onClick: () => handleStatusChange(selectedTestimonial._id, 'approved'),
                        success: true,
                    }] : []),
                    ...(selectedTestimonial.status !== 'rejected' ? [{
                        key: 'rejeitar',
                        label: 'Rejeitar',
                        icon: <XCircle size={20} className="icon" />,
                        onClick: () => handleStatusChange(selectedTestimonial._id, 'rejected'),
                    }] : []),
                    {
                        key: 'editar',
                        label: 'Editar',
                        icon: <Edit size={20} className="icon" />,
                        onClick: () => handleEdit(selectedTestimonial),
                    },
                    {
                        key: 'excluir',
                        label: 'Excluir',
                        icon: <Trash2 size={20} className="icon" />,
                        onClick: () => handleDelete(selectedTestimonial._id),
                        destructive: true,
                    },
                ] : []}
            />
        </div>
    );
};

export default TestimonialsManagementPage;